import React, { useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { Box, Button, Typography } from '@mui/material';
import { Paid as PayIcon } from '@mui/icons-material';

import { roleNames, usePaymentMethodStore, useSessionStore, useUserMessageStore } from '../../../stores';

import PageHeader from '../../../components/common/PageHeader';
import { calculateInvoiceSurcharges, InvoiceListItem, InvoicesPaymentCommand } from '../../../apis/invoiceApi';
import AddPaymentMethodModal from '../../../components/modals/paymentMethod/AddPaymentMethodModal';
import PayInvoicesModal from './PayInvoicesModal';

interface InvoiceListHeaderProps {
  isLoading: boolean;
  rows: InvoiceListItem[];
  onPaymentsSubmitted: (request: InvoicesPaymentCommand) => void;
  onPaymentMethodAdding: () => void;
  onPaymentMethodAddCompleted: () => void;
}

function InvoiceListHeader({ rows, onPaymentsSubmitted, isLoading, onPaymentMethodAdding, onPaymentMethodAddCompleted }: InvoiceListHeaderProps) {
  const paymentsDisabledMessage = 'Payments are disabled. Ask your pool service provider about enabling billing features.';
  const zeroBalanceMessage = 'All invoices are paid!';
  const payInvoicesMessage = 'Select and pay invoices';

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));

  const retrievePaymentMethods = usePaymentMethodStore.use.retrievePaymentMethods();
  const newPaymentMethodId = usePaymentMethodStore.use.newPaymentMethodId();
  const pollForNewMethod = usePaymentMethodStore.use.pollForNewMethod();
  const resetMethodIntent = usePaymentMethodStore.use.resetMethodIntent();
  const intentIsAutopay = usePaymentMethodStore.use.intentIsAutopay();
  const selectAutopayMethod = usePaymentMethodStore.use.selectAutopayMethod();
  const currentCustomer = useSessionStore.use.currentCustomer();
  const hasRoles = useSessionStore.use.hasRoles();
  const setUserMessage = useUserMessageStore.use.setUserMessage();

  const [canPayNow, setCanPayNow] = React.useState(false);
  const [invoiceBalance, setInvoiceBalance] = React.useState(0);
  const [addPaymentMethodVisible, setAddPaymentMethodVisible] = React.useState(false);
  const [payInvoicesVisible, setPayInvoicesVisible] = React.useState(false);

  useEffect(() => {
    if (hasRoles(roleNames.billing, roleNames.paymentMethod)) {
      setCanPayNow(true);
    } else {
      setCanPayNow(false);
    }
  }, [currentCustomer]);

  useEffect(() => {
    setInvoiceBalance(rows.reduce((acc, row) => acc + row.amountDue, 0));
  }, [rows]);

  useEffect(() => {
    if (newPaymentMethodId) {
      onPaymentMethodAdding();
      pollForNewMethod()
        .then((response) => {
          if (response.exists) {
            if (intentIsAutopay && !response.isAutopay) {
              selectAutopayMethod(newPaymentMethodId)
                .then(() => {
                  setUserMessage('Payment method added', 'success', true);
                  retrievePaymentMethods(true);
                })
                .finally(() => {
                  resetMethodIntent();
                  setPayInvoicesVisible(true);
                  onPaymentMethodAddCompleted();
                });
            }
            retrievePaymentMethods(true);
            setUserMessage('Payment method added', 'success', true);
          } else {
            setUserMessage("We're still waiting on the payment method, try refreshing in a few moments", 'info');
          }
        })
        .finally(() => {
          resetMethodIntent();
          setPayInvoicesVisible(true);
          onPaymentMethodAddCompleted();
        });
    }
  }, [newPaymentMethodId]);

  const closeAddPaymentMethod = () => {
    setAddPaymentMethodVisible(false);
  };

  const closePayInvoices = () => {
    setPayInvoicesVisible(false);
  };

  const handlePerformPayment = (command: InvoicesPaymentCommand) => {
    setPayInvoicesVisible(false);
    onPaymentsSubmitted(command);
  };

  const handlePayClick = async () => {
    //See if the user has a payment method
    const paymentMethods = await retrievePaymentMethods(true);
    if (hasRoles(roleNames.surcharging)) {
      await calculateInvoiceSurcharges(rows);
    }
    if (paymentMethods.length === 0) {
      setAddPaymentMethodVisible(true);
    } else {
      setPayInvoicesVisible(true);
    }
  };

  const handleAddPaymentMethod = () => {
    setAddPaymentMethodVisible(true);
  };

  const handleSavingPaymentMethod = () => {
    setUserMessage('Adding payment method', 'info', true);
    closeAddPaymentMethod();
  };

  return (
    <>
      <PayInvoicesModal
        isOpen={payInvoicesVisible}
        isLoading={isLoading}
        allowSurcharging={hasRoles(roleNames.surcharging)}
        onCancel={closePayInvoices}
        onPaymentsSubmitted={handlePerformPayment}
        invoices={rows}
        onAddPaymentMethodClick={handleAddPaymentMethod}
      />
      <AddPaymentMethodModal isOpen={addPaymentMethodVisible} onCancel={closeAddPaymentMethod} onSave={handleSavingPaymentMethod} />
      <PageHeader title="Invoices">
        <Box position="relative" top={1}>
          <Typography variant="h5" position="relative" top={1} mr={isSmUp ? 4 : 3} display="inline-block">
            {isSmUp && 'Total '} Owed: ${invoiceBalance.toFixed(2)}
          </Typography>
          <Box display="inline-block" title={!canPayNow ? paymentsDisabledMessage : invoiceBalance === 0 ? zeroBalanceMessage : payInvoicesMessage}>
            <Button
              disabled={isLoading || !canPayNow || invoiceBalance === 0}
              title={!canPayNow ? paymentsDisabledMessage : invoiceBalance === 0 ? zeroBalanceMessage : payInvoicesMessage}
              variant="contained"
              color="success"
              startIcon={<PayIcon />}
              onClick={handlePayClick}
              sx={{ position: 'relative', top: { xs: '-3px', sm: '-5px' } }}
            >
              Pay
              {isSmUp && ' Now'}
            </Button>
          </Box>
        </Box>
      </PageHeader>
    </>
  );
}

export default InvoiceListHeader;
